/**
 * Visual brand configuration for white-label builds.
 *
 * Product identity lives in `@streamlain/brand`; logo geometry, colors and
 * lockup assets remain here. Runtime surfaces consume the hash-bound vector
 * geometry in `streamlain-brand-assets.json`. App icons and favicons retain
 * their separate distribution pipeline.
 *
 * Schicht 2 (future): runtime DB-per-workspace branding. Not implemented.
 */

import { BRAND_IDENTITY } from '@streamlain/brand';

import { STREAMLAIN_CORE_LOGO } from './streamlain-logo-mark';

export interface BrandColors {
  /** Primary brand color (ring stroke, accents) */
  primary: string;
  /** Darker shade for the mark interior */
  primaryDark: string;
  /** Light variant for dark-mode fallback */
  primaryLight: string;
}

export interface BrandLogoGeometry {
  viewBox: string;
}

export interface BrandWordmarkGeometry {
  /** Wordmark viewBox */
  viewBox: string;
  /** Default display size (width, height) in px */
  defaultSize: readonly [number, number];
}

export interface BrandConfig {
  /** Internal name (kebab-case, used in build scripts) */
  name: string;
  /** Display name (shown in UI) */
  displayName: string;
  /** Short tagline shown under the logo on auth pages */
  tagline: string;
  colors: BrandColors;
  logo: BrandLogoGeometry;
  wordmark: BrandWordmarkGeometry;
}

/**
 * StreamLain default brand — Flow-S mark and vector wordmark.
 *
 * Source of truth:
 *   STREAMLAIN_CORE_LOGO in streamlain-logo-mark.ts
 *
 * Both the React renderer and the pre-React boot renderer consume the same
 * hash-bound geometry in streamlain-brand-assets.json.
 */
export const streamlain: BrandConfig = {
  name: BRAND_IDENTITY.id,
  displayName: BRAND_IDENTITY.displayName,
  tagline: 'Your data stays in the EU. Encrypted. Protected. Always available.',
  colors: {
    primary: STREAMLAIN_CORE_LOGO.colors.primary,
    primaryDark: STREAMLAIN_CORE_LOGO.colors.secondary,
    primaryLight: '#A8D4FF',
  },
  logo: {
    viewBox: STREAMLAIN_CORE_LOGO.mark.viewBox,
  },
  wordmark: {
    viewBox: STREAMLAIN_CORE_LOGO.wordmark.viewBox,
    defaultSize: [
      STREAMLAIN_CORE_LOGO.wordmark.defaultWidth,
      Math.round(
        STREAMLAIN_CORE_LOGO.wordmark.defaultWidth *
          (STREAMLAIN_CORE_LOGO.wordmark.height /
            STREAMLAIN_CORE_LOGO.wordmark.width)
      ),
    ],
  },
};

/** Active brand (swap here for white-label rebrand). */
export const brandConfig: BrandConfig = streamlain;
